import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { CheckmarkCircle02Icon, Tick02Icon } from 'hugeicons-react';
import { useAuth } from '../contexts/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics';
import '../Styles/PaymentConfirmation.css';
import useDocumentTitle from '../UseDocumentTitle';
import { getTier } from '../Data/PricingData';

// Stripe redirects here before its webhook has necessarily reached us, so the
// subscription can take a few seconds to appear.
const SYNC_TIMEOUT_MS = 20000;

const formatDate = (seconds: number) =>
  new Date(seconds * 1000).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

const ConfirmationPage = () => {
  useDocumentTitle('Welcome to StudyBuddy');
  const queryClient = useQueryClient();
  const { subscription } = useAuth();
  const { trackSubscriptionChanged } = useAnalytics();
  const [timedOut, setTimedOut] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    queryClient.clear();
    const t = setTimeout(() => setTimedOut(true), SYNC_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [queryClient]);

  useEffect(() => {
    if (!subscription || tracked.current) return;
    tracked.current = true;
    trackSubscriptionChanged({ action: 'upgraded', fromPlan: 'free', toPlan: subscription.plan });
  }, [subscription, trackSubscriptionChanged]);

  const plan = getTier(subscription?.plan);

  if (!subscription || !plan) {
    return (
      <div className="confirmation-container">
        <div className="confirmation-card" aria-live="polite">
          {timedOut ? (
            <>
              <div className="confirmation-icon"><CheckmarkCircle02Icon size={28} aria-hidden /></div>
              <h1>Payment Received</h1>
              <p className="confirmation-subtitle">
                Your plan can take a minute to show up. You can start using Buddy now; it will switch over automatically.
              </p>
              <div className="confirmation-actions">
                <Link to="/" className="pricing-button is-primary">Start Chatting</Link>
              </div>
            </>
          ) : (
            <>
              <div className="confirmation-spinner" aria-hidden />
              <h1>Activating Your Plan</h1>
              <p className="confirmation-subtitle">This usually takes a few seconds.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const trialEnd = subscription.status === 'trialing' ? subscription.trialEnd : undefined;

  return (
    <div className="confirmation-container">
      <div className="confirmation-card">
        <div className="confirmation-icon"><CheckmarkCircle02Icon size={28} aria-hidden /></div>
        <h1>Welcome to {plan.name}</h1>
        <p className="confirmation-subtitle">
          {trialEnd
            ? `Your free trial is active until ${formatDate(trialEnd)}. Cancel before then and you won't be charged.`
            : 'Your plan is active. Thanks for supporting StudyBuddy.'}
        </p>

        <ul className="confirmation-features">
          {plan.features.map(feature => (
            <li key={feature}>
              <Tick02Icon size={16} aria-hidden />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="confirmation-actions">
          <Link to="/" className="pricing-button is-primary">Start Chatting</Link>
          {plan.limits.spaces && (
            <Link to="/spaces" className="pricing-button">Create a Space</Link>
          )}
        </div>
        <p className="confirmation-footnote">
          Manage billing any time from <Link to="/settings/account">Settings</Link>.
        </p>
      </div>
    </div>
  );
};

export default ConfirmationPage;
